import _ from 'underscore';
import Uri from 'urijs';
import React from 'react';
import PropTypes from 'prop-types';
import { QueryParam, withScope } from '@bingads-webui-react/scope-service';
import { getPathName } from '@bingads-webui-campaign-react/page-menu';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withSettingsConsumer } from '@bingads-webui-react/settings-context';
import { withDefaultLandingPage } from '@bingads-webui-campaign-react/with-default-landing-page';
import { withCurrentExpandedScopeNoFallback } from '@bingads-webui-campaign-react/use-current-scope-entity';
import {
  withSiteMap,
  processorSet,
  findSiteMap,
  Scope,
  getCurrentScope,
} from '@bingads-webui-campaign-react/with-site-map';
import { isBillingLinkPermission } from '@bingads-webui/multi-agency-utils';
import { redirectMap } from './redirects';
import { isLegacyReportingUrl, redirectReportingUrl } from './redirect-reporting-url';
import { withTopTwoCampaigns } from '../data-fetch/with-top-two-campaigns';

class FallbackUpdater extends React.PureComponent {
  static getDerivedStateFromProps({ siteMap }) {
    return { siteMap };
  }

  state = {
    siteMap: [],
  };

  componentDidMount() {
    this.checkFallback();
  }

  componentDidUpdate() {
    this.checkFallback();
  }

  getDefaultRedirectPageId = () => {
    const { defaultLandingPage, scope } = this.props;
    const {
      values: {
        tabredirectid: tabRedirectId,
      },
    } = scope;

    // if tabredirectid is provide, get default landing page with redirects maps
    // redirects maps should be consist /StaticResources/Advertiser/StaticResourcesWeb/Application/Scripts/module/app/router/redirects
    return tabRedirectId && _.has(redirectMap, tabRedirectId) ? redirectMap[tabRedirectId] : defaultLandingPage;
  };

  findPageById = id => findSiteMap(this.state.siteMap, node => node.id === id);

  gotoPage = (pageItem) => {
    if (!_.isEmpty(pageItem)) {
      const { scope: { history } } = this.props;
      const uri = new Uri(pageItem.route);
      history.replace({
        pathname: uri.is('absolute') ? pageItem.route : getPathName(pageItem),
        search: history.location.search,
      });
    }
  };

  gotoPageById = _.compose(this.gotoPage, this.findPageById);

  fallbackToDefaultPage() {
    const {
      scope: {
        values: {
          aid,
          campaignId,
          adGroupId,
          storeId,
          feedId,
        },
        location: {
          pathname,
        },
        history,
      },
      currentCampaign,
      currentAdGroup,
      permissions,
      settingsContext,
    } = this.props;
    const currentScope = getCurrentScope(campaignId, adGroupId, aid, storeId, feedId);
    const isNewReportingUI = _.get(permissions, ['dynamic', 'CustomReports']);

    if (!_.get(permissions, 'IsInternalUser') && isBillingLinkPermission(settingsContext)) {
      this.gotoPageById('unaccessible');
      // For smart-campaigns, it will be the only option, so always fallback to it
    } else if (_.findWhere(this.state.siteMap, { id: 'smart-campaigns' })
      // budget center can be accessed by smart campaign
      && !pathname.toLowerCase().startsWith('/campaign/vnext/budgetcenter')) {
      this.gotoPageById('smart-campaigns');
    } else if (pathname.startsWith('/campaign/vnext/merchantcenter')) {
      let validSiteMapItem;
      if (Scope.Store === currentScope) {
        validSiteMapItem = this.findPageById('mmc-store-summary-productissues');
      } else {
        validSiteMapItem = this.findPageById('merchant-center');
      }
      this.gotoPage(validSiteMapItem);
    } else /* istanbul ignore next */ if (isLegacyReportingUrl(pathname, isNewReportingUI)) {
      redirectReportingUrl(pathname, history, isNewReportingUI);
    } else if (pathname.startsWith('/campaign/vnext/extensions')) {
      const extenionsPage = this.findPageById('extensions');
      if (extenionsPage) {
        this.gotoPage(extenionsPage);
      } else {
        this.gotoPageById(this.getDefaultRedirectPageId());
      }
    } else if (Scope.AdGroup === currentScope) {
      const { CampaignType } = currentCampaign;
      if (CampaignType === 'Shopping') {
        const productGroupsPageItem = this.findPageById('product-groups');
        this.gotoPage(productGroupsPageItem);
      } else if (CampaignType === 'Hotel') {
        let hotelGroupsPageItem = this.findPageById('hotel-groups');
        const IsRenameHotelToLodgingEnabled = _.get(permissions, ['dynamic', 'IsRenameHotelToLodgingEnabled']);
        if (IsRenameHotelToLodgingEnabled) {
          hotelGroupsPageItem = this.findPageById('property-groups');
        }
        this.gotoPage(hotelGroupsPageItem);
      } else if (CampaignType === 'PerformanceMax') {
        const assetGroupsPageItem = this.findPageById('asset-groups');
        this.gotoPage(assetGroupsPageItem);
      } else if (_.result(currentAdGroup, 'AdGroupType') === 'SearchDynamic') {
        this.gotoPageById('dsa');
      } else {
        const keywordPageItem = this.findPageById('keywords');
        if (keywordPageItem) {
          this.gotoPage(keywordPageItem);
        } else {
          this.gotoPageById('ads-and-extensions');
        }
      }
    } else if (Scope.Campaign === currentScope) {
      this.gotoPageById('overview'); // Campaign Overview
    } else if (Scope.Account === currentScope) {
      this.gotoPageById(this.getDefaultRedirectPageId());
    } else {
      this.gotoPageById('accounts');
    }
  }

  checkScopeIds({ currentCampaign, currentAdGroup, scope }) {
    let scopeChanged = false;
    if (scope.values && scope.values.campaignId && !currentCampaign) {
      scope.remove('campaignId');
      scopeChanged = true;
    }
    if (scope.values && scope.values.adGroupId && !currentAdGroup) {
      scope.remove('adGroupId');
      scopeChanged = true;
    }
    if (scopeChanged) {
      scope.history.replace({
        pathname: scope.location.pathname,
        search: scope.stringify(),
      });
    }
  }

  checkFallback() {
    if (!_.findWhere(this.state.siteMap, { selected: true })) {
      this.fallbackToDefaultPage();
      return;
    }
    const { scope, topTwoCampaigns } = this.props;
    const topTwoCampaignsValue = _.get(topTwoCampaigns, 'value', []);
    // Redirect from all campaigns overview to single campaign overview if PMaxLite pilot is enabled and only one campaign
    if (!_.get(scope, ['values', 'campaignId'], false)
      && _.size(topTwoCampaignsValue) === 1
      && _.get(_.first(topTwoCampaignsValue), 'CampaignType') === 'PerformanceMax'
      && _.findWhere(this.state.siteMap, { selected: true, route: 'overview' })) {
      scope.set({ ...scope.values, campaignId: _.get(_.first(topTwoCampaignsValue), 'Id') });
      scope.history.replace({
        pathname: scope.location.pathname,
        search: scope.stringify(),
      });
      return;
    }

    this.checkScopeIds(this.props);
  }

  render() {
    return null;
  }
}

FallbackUpdater.defaultProps = {
  defaultLandingPage: null,
  currentCampaign: null,
  currentAdGroup: null,
};

FallbackUpdater.propTypes = {
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  defaultLandingPage: PropTypes.string,
  currentCampaign: PropTypes.shape({
    CampaignType: PropTypes.string.isRequired,
  }),
  currentAdGroup: PropTypes.shape({}),
  topTwoCampaigns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  settingsContext: PropTypes.shape({}).isRequired,
};

export const FallbackUpdaterWithDefaultLandingPage = _.compose(
  withPermissions,
  withSettingsConsumer,
  withCurrentExpandedScopeNoFallback,
  WrappedComponent => withSiteMap(WrappedComponent, processorSet.all),
  withScope,
  withTopTwoCampaigns,
  withDefaultLandingPage
)(FallbackUpdater);
